import _jsx from "@babel/runtime/helpers/jsx";
var _LoadingSpinner;
import PropTypes from 'prop-types';
import React, { lazy, Suspense, useEffect } from 'react';
import { useFeatures } from 'reaxl-features';
import { useDevice } from '@atc/react-device';
import { injectScript } from '@atc/dom-fns';
import hasDom from 'has-dom';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';
import { LoadingSpinner } from 'reaxl';
import OwnerChatGubagoo from './OwnerChatGubagoo/index.js';
import OwnerChatCarNow from './OwnerChatCarNow/index.js';
const OwnerChatActivEngage = /*#__PURE__*/lazy(() => import(/* webpackPrefetch: true */
/* webpackChunkName: "OwnerChatActivEngage" */
'./OwnerChatActivEngage/index.js'));
export const gubagooDefaultUrl = 'https://cdn.gubagoo.io/toolbars/integration/AT_';
export const renderChat = (provider, _ref) => {
  let {
    gubagooUrl,
    ...props
  } = _ref;
  /**
   * Note: Default provider is 'ACTIVENGAGE'
   * The component has it's own injected scripts and UI that we render
   */
  switch (provider) {
    case 'CARNOW':
      return /*#__PURE__*/React.createElement(OwnerChatCarNow, props);
    case 'GUBAGOO':
      if (gubagooUrl !== gubagooDefaultUrl) {
        return /*#__PURE__*/React.createElement(OwnerChatGubagoo, Object.assign({}, props, {
          gubagooUrl: gubagooUrl
        }));
      }
      return null;
    case 'ACTIVENGAGE':
    default:
      return /*#__PURE__*/_jsx(Suspense, {
        fallback: _LoadingSpinner || (_LoadingSpinner = /*#__PURE__*/_jsx(LoadingSpinner, {
          className: "display-flex align-items-center margin-left-0 margin-top-0",
          height: 40
        }))
      }, void 0, /*#__PURE__*/React.createElement(OwnerChatActivEngage, props));
  }
};
export const injectChat = (provider, gubagooUrl, merchantId) => {
  /**
   * Note: If chat provider is 'GUBAGOO' we don't render anything.
   * Instead, we inject a script, and that script renders the chat UI
   */
  if (provider === 'GUBAGOO') {
    if (!gubagooUrl) {
      gubagooUrl = gubagooDefaultUrl;
    }
    if (gubagooUrl !== gubagooDefaultUrl) {
      return;
    }
    const gubagooSrc = `${gubagooUrl}${merchantId}.js`;
    if (window.gubagooReloadToolbar) {
      window.gubagooReloadToolbar(gubagooSrc);
    } else {
      injectScript(gubagooSrc).catch(error => {
        // eslint-disable-next-line no-console
        console.error(`Error injecting ${provider} script: `, error);
        if (window && window.newrelic) {
          window.newrelic.addPageAction('ThirdPartyError', {
            thirdPartyName: provider,
            component: 'OwnerChat',
            message: `Error injecting ${provider} script`,
            status: error?.status || 'unknown',
            url: window.location.href,
            error
          });
        }
      });
    }
  }
};

/**
 *  OwnerChat component centralizes the functionality to determine what chat provider
 *  is being used by the dealer. It is also responsible for embedding the
 *  Chat Provider's Overlay engagement markup dynamically. 'ACTIVENGAGE' is the default provider,
 *  but others can be easily added.
 */
const OwnerChat = /*#__PURE__*/React.memo(_ref2 => {
  let {
    analyticsData = {},
    chatClickHandler = () => {},
    disableGubagoo = false,
    externalOwnerId = '',
    listing = {},
    merchantId = '',
    provider,
    showChatCTA = true,
    overrideMobileConfig = false,
    ...rest
  } = _ref2;
  const {
    chat: [isChatEnabled, {
      providers: chatProviders,
      gubagoo_base_url: gubagooBaseUrl,
      enable_mobile_chat: enableMobileChat
    }]
  } = useFeatures(['chat']);
  const device = useDevice();
  const isXs = _get(device, 'is.xs', false);
  const gubagooUrl = provider === 'GUBAGOO' ? gubagooBaseUrl + (externalOwnerId ?? '') : '';
  const listingId = listing?.id || '';
  const showChat = !!provider // has provider
  // Enable FF
  && isChatEnabled
  // provider is matched with FF variable
  && chatProviders?.includes(String(provider))
  // For Gubagoo, hide chat if disableGubagoo is true (default for VDP ownerDetails)
  // For other providers, hide chat on mobile device unless FF variable enableMobileChat is true or overrideMobileConfig is true
  && (provider === 'GUBAGOO' ? !disableGubagoo : enableMobileChat || !isXs || overrideMobileConfig);
  useEffect(() => {
    injectChat(provider, gubagooUrl, merchantId);
    return () => window.gubagooReloadToolbar && window.gubagooReloadToolbar('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingId]);
  if (!showChat) {
    return null;
  }
  const onChatClick = () => {
    switch (provider) {
      case 'ACTIVENGAGE':
        window?.chatTextEventCallback('chatCTA', {
          ...analyticsData,
          co_txt_url: 'Chat with Dealer'
        });
        break;
      case 'CARNOW':
        window?._sendCustomAnalyticEvent({
          events: 'chatClick',
          type: 'chatCTA'
        });
        break;
      default:
        window?.chatTextEventCallback('chatCTA', analyticsData);
    }
    chatClickHandler();
  };
  const props = {
    listing,
    gubagooUrl,
    merchantId,
    onChatClick,
    showChatCTA,
    ...rest
  };
  return hasDom() ? renderChat(provider, props) : null;
}, (newProps, oldProps) => _isEqual(newProps, oldProps));
export default OwnerChat;